import React, { useEffect, useState } from "react";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMPageLoader from "src/components/UI/atoms/ATMPageLoader/ATMPageLoader";
import {
  useGetPcoFirmsQuery,
  useUpdatePcoFirmMutation,
  useUploadPcoFirmSealMutation,
} from "src/services/PcoFirmService";
import { PcoFirm } from "src/pages/employer-certificate/pcoFirms";
import { showToast } from "src/utils/toaster/showToast";

/**
 * PCO Firm Details — one card per PCO firm.
 *
 * Each firm's "Details of the Individual issuing the certificate" and its
 * seal & signature image, as the Employer Certificate prints them for an
 * application filed under that firm (the firm on the PAN form's office
 * address). The five firms are fixed; only their details change here.
 */

type FirmFields = Pick<
  PcoFirm,
  | "fullName"
  | "designation"
  | "panNumber"
  | "officeAddressWithLocation"
  | "telephone"
  | "mobile"
>;

const FIELDS: { key: keyof FirmFields; label: string; maxLength?: number }[] = [
  { key: "fullName", label: "Full Name" },
  { key: "designation", label: "Designation" },
  { key: "panNumber", label: "PAN of the Individual", maxLength: 10 },
  { key: "officeAddressWithLocation", label: "Office Address with Location" },
  { key: "telephone", label: "Telephone" },
  { key: "mobile", label: "Mobile", maxLength: 10 },
];

const toFields = (firm: PcoFirm): FirmFields => ({
  fullName: firm.fullName || "",
  designation: firm.designation || "",
  panNumber: firm.panNumber || "",
  officeAddressWithLocation: firm.officeAddressWithLocation || "",
  telephone: firm.telephone || "",
  mobile: firm.mobile || "",
});

const inputClass =
  "w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-main";

const FirmCard = ({ firm }: { firm: PcoFirm }) => {
  const [values, setValues] = useState<FirmFields>(toFields(firm));
  const [updateFirm, updateInfo] = useUpdatePcoFirmMutation();
  const [uploadSeal, uploadInfo] = useUploadPcoFirmSealMutation();
  const [uploadLetterhead, letterheadInfo] = useUploadPcoFirmSealMutation();

  useEffect(() => setValues(toFields(firm)), [firm]);

  const isDirty = FIELDS.some(({ key }) => values[key] !== (firm[key] || ""));

  const handleSave = () => {
    updateFirm({ id: firm._id, body: values }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to save.");
      } else {
        showToast("success", res?.data?.message);
      }
    });
  };

  const handleSeal = (file?: File | null) => {
    if (!file) return;
    const body = new FormData();
    body.append("image", file);
    uploadSeal({ id: firm._id, body }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to upload.");
      } else {
        showToast("success", res?.data?.message);
      }
    });
  };

  const handleLetterhead = (file?: File | null) => {
    if (!file) return;
    const body = new FormData();
    body.append("image", file);
    uploadLetterhead({ id: firm._id, body, kind: "letterhead" }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || res?.data?.message || "Unable to upload.");
      } else {
        showToast("success", res?.data?.message);
      }
    });
  };

  const handleRemoveLetterhead = () => {
    updateFirm({ id: firm._id, body: { letterheadImage: "" } }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || "Unable to remove.");
      } else {
        showToast("success", "Letterhead removed.");
      }
    });
  };

  const handleRemoveSeal = () => {
    updateFirm({ id: firm._id, body: { sealSignatureImage: "" } }).then((res: any) => {
      if (res?.error || !res?.data?.status) {
        showToast("error", res?.error?.data?.message || "Unable to remove.");
      } else {
        showToast("success", "Seal & signature removed.");
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4 flex flex-col gap-4">
      <div className="font-semibold text-primary-main text-base border-b pb-2">
        {firm.firmName}
      </div>

      <div className="flex gap-6 flex-wrap">
        <div className="flex-1 min-w-[280px] grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELDS.map(({ key, label, maxLength }) => (
            <label key={key} className={key === "officeAddressWithLocation" ? "md:col-span-2" : ""}>
              <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
              <input
                type="text"
                className={inputClass}
                value={values[key]}
                maxLength={maxLength}
                onChange={(e) => {
                  let value = e.target.value;
                  if (key === "panNumber") value = value.toUpperCase();
                  if (key === "mobile") value = value.replace(/\D/g, "");
                  setValues((prev) => ({ ...prev, [key]: value }));
                }}
              />
            </label>
          ))}
        </div>

        <div className="w-[240px] flex flex-col gap-2">
          <span className="block text-xs font-medium text-slate-600">Seal &amp; Signature</span>
          <div
            className="h-[150px] border border-dashed border-slate-400 rounded flex items-center justify-center overflow-hidden"
            style={{
              // checkerboard so a transparent PNG is visibly transparent
              backgroundImage:
                "linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)",
              backgroundSize: "16px 16px",
              backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
            }}
          >
            {firm.sealSignatureImage ? (
              <img
                src={firm.sealSignatureImage}
                alt="Seal & signature"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span className="text-xs text-slate-500 px-3 text-center">No seal &amp; signature uploaded</span>
            )}
          </div>
          <label className="cursor-pointer text-center text-sm border border-primary-main text-primary-main rounded py-1.5 hover:bg-slate-50">
            {uploadInfo.isLoading ? "Uploading..." : firm.sealSignatureImage ? "Change Image" : "Upload Image"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={uploadInfo.isLoading}
              onChange={(e) => {
                handleSeal(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
          {firm.sealSignatureImage && (
            <button
              type="button"
              className="text-xs text-red-600 underline"
              onClick={handleRemoveSeal}
              disabled={updateInfo.isLoading}
            >
              Remove image
            </button>
          )}
          <span className="text-[11px] text-slate-500 leading-snug">
            Use a PNG with a transparent background — it is printed over the
            applicant's photo.
          </span>
        </div>

        <div className="w-[180px] flex flex-col gap-2">
          <span className="block text-xs font-medium text-slate-600">Letterhead</span>
          <div className="h-[250px] border border-dashed border-slate-400 rounded flex items-center justify-center overflow-hidden">
            {firm.letterheadImage ? (
              <img
                src={firm.letterheadImage}
                alt="Letterhead"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span className="text-xs text-slate-500 px-3 text-center">No letterhead uploaded</span>
            )}
          </div>
          <label className="cursor-pointer text-center text-sm border border-primary-main text-primary-main rounded py-1.5 hover:bg-slate-50">
            {letterheadInfo.isLoading ? "Uploading..." : firm.letterheadImage ? "Change Letterhead" : "Upload Letterhead"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={letterheadInfo.isLoading}
              onChange={(e) => {
                handleLetterhead(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
          {firm.letterheadImage && (
            <button
              type="button"
              className="text-xs text-red-600 underline"
              onClick={handleRemoveLetterhead}
              disabled={updateInfo.isLoading}
            >
              Remove letterhead
            </button>
          )}
          <span className="text-[11px] text-slate-500 leading-snug">
            Blank A4 page, upright (header at the top). The certificate is
            printed on it — no address is typed above.
          </span>
        </div>
      </div>

      <div className="flex justify-end">
        <ATMLoadingButton
          className="bg-primary-main w-auto px-6"
          onClick={handleSave}
          isLoading={updateInfo.isLoading}
          disabled={!isDirty || updateInfo.isLoading}
        >
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

const PcoFirmDetails = () => {
  const { data, isLoading } = useGetPcoFirmsQuery(undefined);
  const firms: PcoFirm[] = data?.data || [];

  return (
    <SideNavLayout>
      <div className="p-4 flex flex-col gap-4 h-full overflow-auto">
        <div>
          <div className="text-xl font-medium">PCO Firm Details</div>
          <div className="text-sm text-slate-600">
            Issuer details and seal &amp; signature printed on the Employer
            Certificate of a PCO application, per firm.
          </div>
        </div>
        {isLoading ? (
          <ATMPageLoader />
        ) : firms.length ? (
          firms.map((firm) => <FirmCard key={firm._id} firm={firm} />)
        ) : (
          <div className="text-sm text-slate-500">No firms found.</div>
        )}
      </div>
    </SideNavLayout>
  );
};

export default PcoFirmDetails;
